import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense, useState, useEffect } from "react";
import Bugsnag from "./utils/bugsnag.js";
import { Spinner } from "./components/spinner/Spinner.js";
import { MobileWarningDialog } from "./components/mobile-warning-dialog/MobileWarningDialog.js";
import { isMobile } from "./utils/deviceUtils.js";
import React from "react";
import { Route, Routes } from "react-router-dom";

// Lazy load heavy components for better initial bundle size
const Home = lazy(() =>
  import("./pages/home/Home.js").then((module) => ({ default: module.Home }))
);
const MarketAnalysis = lazy(
  () => import("./pages/market-analysis/MarketAnalysis.js")
);

// Error fallback component as recommended in official docs
const ErrorFallback = () => {
  return (
    <div
      style={{
        padding: "20px",
        textAlign: "center",
        fontFamily: "Arial, sans-serif",
        color: "#666",
      }}
    >
      <h2>Something went wrong</h2>
      <p>
        We're sorry, but something unexpected happened. Please try refreshing the
        page.
      </p>
      <button
        onClick={() => {
          window.location.reload();
        }}
        style={{
          padding: "10px 20px",
          backgroundColor: "#007bff",
          color: "white",
          border: "none",
          borderRadius: "4px",
          cursor: "pointer",
        }}
      >
        Refresh Page
      </button>
    </div>
  );
};

// Create error boundary using Bugsnag React plugin
// Only create if Bugsnag is properly initialized
// Per official docs: https://docs.bugsnag.com/platforms/javascript/react/
const createErrorBoundary = () => {
  try {
    // Wait a bit for Bugsnag to fully initialize
    const reactPlugin = Bugsnag.getPlugin?.("react");

    if (reactPlugin && typeof reactPlugin.createErrorBoundary === "function") {
      const ErrorBoundary = reactPlugin.createErrorBoundary(React);
      return ErrorBoundary;
    } else {
      return null;
    }
  } catch (error) {
    console.error("Failed to create Bugsnag ErrorBoundary:", error);
    return null;
  }
};

function App() {
  const queryClient = new QueryClient();
  const [showMobileWarning, setShowMobileWarning] = useState(false);

  useEffect(() => {
    // Check if user is on mobile and hasn't dismissed the warning
    const hasSeenMobileWarning = localStorage.getItem('hasSeenMobileWarning');
    
    if (isMobile() && !hasSeenMobileWarning) {
      setShowMobileWarning(true);
    }
  }, []);

  const handleCloseMobileWarning = () => {
    setShowMobileWarning(false);
    localStorage.setItem('hasSeenMobileWarning', 'true');
  };

  // Always render the main content, with or without ErrorBoundary
  const mainContent = (
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={<Spinner />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/market-analysis" element={<MarketAnalysis />} />
        </Routes>
      </Suspense>
      <MobileWarningDialog 
        isOpen={showMobileWarning} 
        onClose={handleCloseMobileWarning} 
      />
    </QueryClientProvider>
  );

  // Try to create ErrorBoundary when component renders
  const ErrorBoundary = createErrorBoundary();

  // If ErrorBoundary is not available, render without it
  if (!ErrorBoundary) {
    return mainContent;
  }

  // Render with ErrorBoundary
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      {mainContent}
    </ErrorBoundary>
  );
}

export default App;
